import { rememberedOrganizationLocation } from '@/shared/config';
import { BusinessLocationStatus } from '@shared/api/graphql/gql/graphql';
import { Temporal, clock, parsePlainDate, todayInTimezone } from '@shared/lib/date-time';

import type { LocationTimezone } from './location-zone';

/** Минимум полей локации, необходимый для решения «закрыта ли она». */
export interface ClosableLocation {
  title: string;
  timezone?: LocationTimezone;
  closed_from?: string | null;
}

/** Запомненная локация организации, если в неё можно войти сразу: включена и не закрыта. */
export const findRememberedOpenLocation = <T extends ClosableLocation & { id: string; enabled?: boolean | null }>(
  organizationId: string,
  locations: readonly T[] | null | undefined,
): T | undefined => {
  const rememberedId = rememberedOrganizationLocation(organizationId);

  return locations?.find(
    (location) => location.id === rememberedId && location.enabled === true && !isLocationClosed(location),
  );
};

/** Общая публичная видимость для настроек и выбора рабочей локации. */
export const isLocationPublic = (
  location: ClosableLocation & { enabled?: boolean; business_status?: BusinessLocationStatus },
  now?: Temporal.Instant,
): boolean =>
  location.enabled !== false &&
  location.business_status !== BusinessLocationStatus.Hidden &&
  !isLocationClosed(location, now);

/**
 * Закрыта ли локация на текущий момент по её локальному времени.
 * Дата закрытия в будущем (запланированное закрытие) считается «ещё работает».
 */
export const isLocationClosed = (location: ClosableLocation, now: Temporal.Instant = clock.now()): boolean => {
  const closedFrom = location.closed_from;

  if (!closedFrom) {
    return false;
  }

  const localToday = todayInTimezone(location.timezone, now);
  const closedDate = parsePlainDate(closedFrom);
  return localToday !== null && closedDate !== null && Temporal.PlainDate.compare(localToday, closedDate) >= 0;
};

/** Заголовок локации с пометкой «(закрыта)» для фильтров истории. */
export const locationTitleWithClosedMark = (location: ClosableLocation, now: Temporal.Instant = clock.now()): string =>
  isLocationClosed(location, now) ? `${location.title} (закрыта)` : location.title;
